import { useCallback, useEffect, useRef, useState } from "react";

export function useKitchenItemAdvanceQueue({
    resolveProductionCenterNameForItem,
    setStatusCommand,
    refetch,
    getNextStatus,
}) {
    const [pendingItemIdsByOrder, setPendingItemIdsByOrder] = useState({});
    const queuedIdsByOrderRef = useRef(new Map());
    const processingOrdersRef = useRef(new Set());
    const flushTimersByOrderRef = useRef(new Map());
    const getQueueKey = useCallback(
        (orderName, productionCenterName) =>
            `${orderName || ""}::${productionCenterName || ""}`,
        []
    );

    const addPendingItem = useCallback((orderName, identifier) => {
        if (!orderName || !identifier) return;
        setPendingItemIdsByOrder((prev) => {
            const current = prev[orderName] || [];
            if (current.includes(identifier)) return prev;
            return {
                ...prev,
                [orderName]: [...current, identifier],
            };
        });
    }, []);

    const removePendingItems = useCallback((orderName, identifiers = []) => {
        if (!orderName || identifiers.length === 0) return;
        setPendingItemIdsByOrder((prev) => {
            const current = prev[orderName] || [];
            if (current.length === 0) return prev;
            const toRemove = new Set(identifiers);
            const remaining = current.filter((id) => !toRemove.has(id));
            if (remaining.length === current.length) return prev;
            const next = { ...prev };
            if (remaining.length > 0) {
                next[orderName] = remaining;
            } else {
                delete next[orderName];
            }
            return next;
        });
    }, []);

    const flushOrderQueue = useCallback(
        (orderName, productionCenterName) => {
            const queueKey = getQueueKey(orderName, productionCenterName);
            if (
                !orderName ||
                !productionCenterName ||
                processingOrdersRef.current.has(queueKey)
            ) {
                return;
            }

            const queueSet = queuedIdsByOrderRef.current.get(queueKey);
            const identifiers = Array.from(queueSet || []);
            if (identifiers.length === 0) return;

            queuedIdsByOrderRef.current.set(queueKey, new Set());
            processingOrdersRef.current.add(queueKey);

            setStatusCommand(
                {
                    production_center: productionCenterName,
                    identifiers,
                    orderName,
                },
                {
                    onSuccess: () => {
                        refetch?.();
                    },
                    onSettled: () => {
                        processingOrdersRef.current.delete(queueKey);
                        removePendingItems(orderName, identifiers);

                        const queuedAfter = queuedIdsByOrderRef.current.get(queueKey);
                        if (queuedAfter && queuedAfter.size > 0) {
                            const timerId = setTimeout(() => {
                                flushOrderQueue(orderName, productionCenterName);
                            }, 0);
                            flushTimersByOrderRef.current.set(queueKey, timerId);
                        }
                    },
                }
            );
        },
        [getQueueKey, refetch, removePendingItems, setStatusCommand]
    );

    const enqueueItemAdvance = useCallback(
        (orderName, identifier, productionCenterName) => {
            const queueKey = getQueueKey(orderName, productionCenterName);
            if (!orderName || !identifier || !productionCenterName) return;

            if (!queuedIdsByOrderRef.current.has(queueKey)) {
                queuedIdsByOrderRef.current.set(queueKey, new Set());
            }
            queuedIdsByOrderRef.current.get(queueKey).add(identifier);
            addPendingItem(orderName, identifier);

            const existingTimer = flushTimersByOrderRef.current.get(queueKey);
            if (existingTimer) clearTimeout(existingTimer);

            const timerId = setTimeout(() => {
                flushOrderQueue(orderName, productionCenterName);
            }, 300);
            flushTimersByOrderRef.current.set(queueKey, timerId);
        },
        [addPendingItem, flushOrderQueue, getQueueKey]
    );

    useEffect(() => {
        return () => {
            flushTimersByOrderRef.current.forEach((timerId) =>
                clearTimeout(timerId)
            );
            flushTimersByOrderRef.current.clear();
            queuedIdsByOrderRef.current.clear();
            processingOrdersRef.current.clear();
        };
    }, []);

    const handleAdvanceItem = useCallback(
        (order, item, items) => {
            const productionCenterName = resolveProductionCenterNameForItem?.(
                item,
                items,
                order
            );
            if (
                !order?.name ||
                !item?.identifier ||
                !productionCenterName ||
                !getNextStatus(item, items, order)
            ) {
                return;
            }
            enqueueItemAdvance(order.name, item.identifier, productionCenterName);
        },
        [
            enqueueItemAdvance,
            getNextStatus,
            resolveProductionCenterNameForItem,
        ]
    );

    const isItemActionDisabled = useCallback(
        (orderName, identifier) => {
            if (!orderName || !identifier) return false;
            const list = pendingItemIdsByOrder[orderName] || [];
            return list.includes(identifier);
        },
        [pendingItemIdsByOrder]
    );

    return {
        pendingItemIdsByOrder,
        handleAdvanceItem,
        isItemActionDisabled,
    };
}
